import {WriteIconButton} from '../common/buttons/WriteActions';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {Box, Chip, Divider, Link, Paper, Skeleton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Tooltip, Typography} from '@mui/material';
import {Link as RouterLink} from 'react-router-dom';
import {AppTranslationFunction} from '@mybake/ui/hooks/useAppTranslation';
import {ROWS_PER_PAGE_OPTIONS} from '../../hooks/usePageLimit';
import {FinanceTransactionType, IFinanceTransaction, IFinanceTransactionsPage} from '@mybake/ui/models/Finances/IFinance';

interface Props {
    page?: IFinanceTransactionsPage;
    pageIndex: number;
    pageSize: number;
    loading: boolean;
    locale: string;
    currency: (value: number) => string;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    onEdit: (transaction: IFinanceTransaction) => void;
    onDelete: (transaction: IFinanceTransaction) => void;
    t: AppTranslationFunction;
}

const typeColor = (type: FinanceTransactionType): 'success' | 'warning' | 'info' | 'default' => {
    if (type === 'ORDER_INCOME') return 'success';
    if (type === 'MANUAL_EXPENSE') return 'warning';
    if (type === 'PRODUCT_PURCHASE' || type === 'PACKAGE_PURCHASE') return 'info';
    return 'default';
};

export const FinanceTransactions = ({
    page,
    pageIndex,
    pageSize,
    loading,
    locale,
    currency,
    onPageChange,
    onPageSizeChange,
    onEdit,
    onDelete,
    t,
}: Props) => {
    const transactions = page?.data ?? [];
    const totalCount = page?.totalCount ?? 0;
    const description = (transaction: IFinanceTransaction) => transaction.orderGuidId ? (
        <Link component={RouterLink} to={`/app/orders/${transaction.orderGuidId}`}>
            {transaction.description || '—'}
        </Link>
    ) : transaction.description || '—';

    const getTypeLabel = (type: FinanceTransactionType) => {
        const labels: Record<FinanceTransactionType, string> = {
            ORDER_INCOME: t('finance:transaction_order'),
            PRODUCT_PURCHASE: t('finance:transaction_product_purchase'),
            PACKAGE_PURCHASE: t('finance:transaction_package_purchase'),
            MANUAL_EXPENSE: t('finance:transaction_manual_expense'),
        };
        return labels[type];
    };

    const formatDate = (value: string) =>
        new Intl.DateTimeFormat(locale, {day: '2-digit', month: 'short', year: 'numeric'}).format(new Date(value));

    return (
        <Paper>
            <Stack sx={{p: {xs: 2, md: 3}, pb: 1}}>
                <Typography variant="h3">{t('finance:transactions_title')}</Typography>
                <Typography variant="body2" color="text.secondary">
                    {t('finance:transactions_hint')}
                </Typography>
            </Stack>
            <Box sx={{display: {xs: 'block', sm: 'none'}, px: 2, pb: 1}}>
                {loading ? (
                    <Stack spacing={1.5} sx={{py: 1}}>
                        {Array.from({length: 4}).map((_, index) => (
                            <Skeleton key={index} variant="rounded" height={112}/>
                        ))}
                    </Stack>
                ) : transactions.length === 0 ? (
                    <Typography color="text.secondary" align="center" sx={{py: 4}}>
                        {t('finance:no_transactions')}
                    </Typography>
                ) : (
                    <Stack divider={<Divider flexItem/>}>
                        {transactions.map((transaction) => {
                            const isIncome = transaction.type === 'ORDER_INCOME';
                            return (
                                <Box key={transaction.id} sx={{py: 2}}>
                                    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.5}>
                                        <Typography variant="caption" color="text.secondary">
                                            {formatDate(transaction.occurredAt)}
                                        </Typography>
                                        <Typography
                                            fontWeight={700}
                                            color={isIncome ? 'success.main' : 'text.primary'}
                                            sx={{whiteSpace: 'nowrap'}}
                                        >
                                            {isIncome ? '+' : '−'} {currency(transaction.amount)}
                                        </Typography>
                                    </Stack>
                                    <Chip
                                        size="small"
                                        color={typeColor(transaction.type)}
                                        label={getTypeLabel(transaction.type)}
                                        sx={{mt: 0.75, maxWidth: '100%'}}
                                    />
                                    <Typography variant="body2" sx={{mt: 1.25, overflowWrap: 'anywhere'}}>
                                        {description(transaction)}
                                    </Typography>
                                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{mt: 0.75}}>
                                        <Typography variant="caption" color="text.secondary" sx={{overflowWrap: 'anywhere'}}>
                                            {transaction.category}
                                        </Typography>
                                        {transaction.isEditable && (
                                            <Box sx={{flexShrink: 0}}>
                                                <WriteIconButton size="small" aria-label={t('finance:edit_expense')} onClick={() => onEdit(transaction)}>
                                                    <EditOutlinedIcon fontSize="small"/>
                                                </WriteIconButton>
                                                <WriteIconButton
                                                    size="small"
                                                    color="error"
                                                    aria-label={t('finance:delete_expense')}
                                                    onClick={() => onDelete(transaction)}
                                                >
                                                    <DeleteOutlineIcon fontSize="small"/>
                                                </WriteIconButton>
                                            </Box>
                                        )}
                                    </Stack>
                                </Box>
                            );
                        })}
                    </Stack>
                )}
            </Box>

            <TableContainer sx={{display: {xs: 'none', sm: 'block'}}}>
                <Table sx={{minWidth: 760}}>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('finance:date')}</TableCell>
                            <TableCell>{t('finance:type')}</TableCell>
                            <TableCell>{t('finance:category')}</TableCell>
                            <TableCell>{t('finance:description')}</TableCell>
                            <TableCell align="right">{t('finance:amount')}</TableCell>
                            <TableCell align="right">{t('finance:actions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            Array.from({length: 5}).map((_, index) => (
                                <TableRow key={index}>
                                    {Array.from({length: 6}).map((__, cell) => (
                                        <TableCell key={cell}>
                                            <Skeleton/>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : transactions.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{py: 5}}>
                                    <Typography color="text.secondary">{t('finance:no_transactions')}</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            transactions.map((transaction) => {
                                const isIncome = transaction.type === 'ORDER_INCOME';
                                return (
                                    <TableRow key={transaction.id}>
                                        <TableCell>
                                            {formatDate(transaction.occurredAt)}
                                        </TableCell>
                                        <TableCell>
                                            <Chip size="small" color={typeColor(transaction.type)} label={getTypeLabel(transaction.type)}/>
                                        </TableCell>
                                        <TableCell>{transaction.category}</TableCell>
                                        <TableCell sx={{maxWidth: 300}}>
                                            <Typography variant="body2" noWrap title={transaction.description}>
                                                {description(transaction)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell
                                            align="right"
                                            sx={{fontWeight: 700, color: isIncome ? 'success.main' : 'text.primary', whiteSpace: 'nowrap'}}
                                        >
                                            {isIncome ? '+' : '−'} {currency(transaction.amount)}
                                        </TableCell>
                                        <TableCell align="right">
                                            {transaction.isEditable ? (
                                                <>
                                                    <Tooltip title={t('finance:edit_expense')}>
                                                        <WriteIconButton size="small" onClick={() => onEdit(transaction)}>
                                                            <EditOutlinedIcon fontSize="small"/>
                                                        </WriteIconButton>
                                                    </Tooltip>
                                                    <Tooltip title={t('finance:delete_expense')}>
                                                        <WriteIconButton size="small" color="error" onClick={() => onDelete(transaction)}>
                                                            <DeleteOutlineIcon fontSize="small"/>
                                                        </WriteIconButton>
                                                    </Tooltip>
                                                </>
                                            ) : (
                                                <Typography variant="caption">—</Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={totalCount}
                page={pageIndex}
                rowsPerPage={pageSize}
                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS as unknown as number[]}
                onPageChange={(_, value) => onPageChange(value)}
                onRowsPerPageChange={(event) => onPageSizeChange(Number(event.target.value))}
            />
        </Paper>
    );
};
